import useSWR from 'swr';
import { messageRoomCacheKeyGenerator } from './cache';
import { MessageRoom, MessageRoomId } from 'src/models/messageRoom';
import { useMessageRoomRepository } from 'src/repositories/messageRoom/repository';
import { MCustomer } from 'src/models/mCustomer';
import { message } from 'antd';
import { useLoginAccount } from 'src/globalStates/loginAccount';
import { QueryDocumentSnapshot } from 'firebase/firestore';

// SearchFilter インターフェースの定義
interface SearchFilter {
  id: string;
  field: 'mCusId' | 'mCusName' | 'nickname';
  label: string;
  value: string;
}

// メッセージルームのリスト取得用のレスポンス型
export type MessageRoomListResponse = { 
  rooms: MessageRoom[], 
  totalCount: number, 
  warning?: string, // 複数フィルター検索時のみ
  firstDoc: QueryDocumentSnapshot | undefined, 
  lastDoc: QueryDocumentSnapshot | undefined 
};

// メッセージルームのリスト取得用のクエリパラメータ型
export type MessageRoomListQuery = {
  mCustomerId: string | null;
  searchFilters?: SearchFilter[];
  isImportant?: boolean;
  firstDoc?: QueryDocumentSnapshot | undefined;
  lastDoc?: QueryDocumentSnapshot | undefined;
};

export const useMessageRoomList = (query: MessageRoomListQuery) => {
  const repository = useMessageRoomRepository();
  const account = useLoginAccount();

  return useSWR<MessageRoomListResponse>(
    messageRoomCacheKeyGenerator.generateListKey({
      query,
      ignore: !account.merchantId,
    }),
    async () => {
      // フィルター検索の場合
      if (query.searchFilters && query.searchFilters.length > 0) {
        if (query.searchFilters.length === 1) {
          // 単一フィルター検索（ページング有効）
          const totalCount = await repository.getCount(query);
          const result = await repository.searchBySingleFilter(query);
          
          return {
            rooms: result.messageRooms,
            totalCount: totalCount,
            firstDoc: result.firstDoc,
            lastDoc: result.lastDoc
          };
        } else {
          // 複数フィルター検索（OR検索、ページング無効）
          const result = await repository.searchByMultipleFilters(query);
          
          return {
            rooms: result.messageRooms,
            totalCount: result.messageRooms.length, // 実際の取得件数
            warning: result.warning,
            firstDoc: undefined, // 複数フィルター検索ではページング無効
            lastDoc: undefined,  // 複数フィルター検索ではページング無効
          };
        }
      }

      // その他の検索は従来通り（ページング有効）
      const totalCount = await repository.getCount(query);
      
      if (query.mCustomerId) {
        // 顧客ID指定：特定顧客のメッセージルーム取得
        const result = await repository.getDocFromMCusId(query);
        return {
          rooms: result.messageRooms,
          totalCount: totalCount,
          firstDoc: result.firstDoc,
          lastDoc: result.lastDoc
        };
      } 
      else if (query.isImportant === true) {
        // 重要度フィルター：星マーク付きメッセージルームのみ取得
        const result = await repository.getDocIsImportant(query);
        return {
          rooms: result.messageRooms,
          totalCount: totalCount,
          firstDoc: result.firstDoc,
          lastDoc: result.lastDoc
        };
      } 
      else {
        // デフォルト：全メッセージルーム取得（権限に応じたフィルタリング適用）
        const result = await repository.getList(query);
        return {
          rooms: result.messageRooms,
          totalCount: totalCount,
          firstDoc: result.firstDoc,
          lastDoc: result.lastDoc
        };
      }
    },
    {
      onError: (_) => {
        message.error('エラーが発生しました。更新して再度お試しください。');
      },
    }
  );
};

export type MessageRoomGetItemQuery = {
  mRoomId: MessageRoomId | null;
};

export type MessageRoomResponse = { room: MessageRoom | null };

export const useMessageRoom = (query: MessageRoomGetItemQuery) => {
  const repository = useMessageRoomRepository();
  const account = useLoginAccount();

  return useSWR<MessageRoomResponse>(
    messageRoomCacheKeyGenerator.generateItemKey({
      query,
      ignore: !query.mRoomId || !account.merchantId,
    }),
    async () => {
      return {
        room: await repository.getDoc(query),
      };
    },
    {
      onError: (_) => {
        message.error('エラーが発生しました。更新して再度お試しください。');
      },
    }
  );
};

export type MessageRoomGetItemResponse = {
  mRoom: MessageRoom | null;
  mCustomer: MCustomer | null;
};

export type { SearchFilter };
